import { useEffect, useRef, useState } from "react";
import type { BuildNumbers } from "./types";

// useSmoothProgress — the percent a build bar DRAWS, as opposed to the percent
// the server last reported. The server only learns something when a picture or
// a clip lands, so its number sits still for a minute and then leaps; a bar
// that copies it looks frozen and the customer leaves (owner report
// 2026-09-28). The drawn value instead:
//   • never goes below the server's progress and never goes backwards;
//   • between real events, walks with the clock toward where the job should be
//     (100 × elapsed / expected_total), clamped to [progress, progress_ceiling]
//     minus a small RESERVE — and never faster than CHASE_MAX_PACE × the job's
//     normal pace, so catching up after a slow start is a glide, not a leap
//     (a redraw used to jump 15 → 61 % in about a second after its cold start);
//   • always keeps closing a small share of the gap to the ceiling every tick,
//     so it slows but never visibly stops. The reserve is what that creep lives
//     on: following the clock straight up to the ceiling left nothing to creep
//     through, and the bar sat on one number for a minute or more whenever the
//     next real event was late (cold start, a slow worker);
//   • never passes progress_ceiling (the percent the NEXT real event brings);
//   • jumps to 100 only when the job is completed.
// With an older server that sends `progress` alone, the ceiling is
// min(99, progress + FALLBACK_HEADROOM) so the bar still breathes.
//
// Every bar of one job (chat, board card, storyboard header) shares one value
// through SHARED, keyed by the job, so the numbers side by side always agree.

export const TICK_MS = 300;
/** Headroom above the server's progress when it sends no ceiling (older server). */
export const FALLBACK_HEADROOM = 8;
/** How fast the drawn value follows the clock-based prediction (ms time constant). */
const TRACK_TAU_MS = 1200;
/** Catching up with the clock never runs faster than this many times the job's
 *  normal pace (100 % over expected_total_ms). */
const CHASE_MAX_PACE = 3;
/** The clock may lead the bar only up to this share of the room between the
 *  server's progress and the ceiling, at most RESERVE_MAX points below it; the
 *  rest is left for the creep, so a late event never meets a bar with nowhere
 *  left to go. */
const RESERVE_SHARE = 0.6;
const RESERVE_MAX = 4;
/** How slowly it always creeps toward the ceiling (ms time constant). */
const CREEP_TAU_MS = 45000;

export type SmoothInputs = {
  progress: number;
  ceiling: number | null;
  expectedTotalMs: number | null;
  /** Elapsed ms as the server reported it, or null when it did not. */
  elapsedMs: number | null;
  /** Local ms when those numbers arrived. */
  receivedAt: number;
  /** Local ms this bar first saw the job — the elapsed clock when the server sends none. */
  localStart: number;
  completed: boolean;
};

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** The highest value the bar may draw right now (below 100 until completed). */
export function effectiveCeiling(inp: Pick<SmoothInputs, "progress" | "ceiling" | "completed">): number {
  if (inp.completed) return 100;
  const p = clamp(inp.progress, 0, 100);
  const c = inp.ceiling !== null ? inp.ceiling : p + FALLBACK_HEADROOM;
  return Math.max(p, clamp(c, 0, 99));
}

/** Elapsed ms of the job at `now` (server clock when known, else this bar's own). */
export function elapsedAt(inp: Pick<SmoothInputs, "elapsedMs" | "receivedAt" | "localStart">, now: number): number {
  if (inp.elapsedMs !== null) return Math.max(0, inp.elapsedMs + Math.max(0, now - inp.receivedAt));
  return Math.max(0, now - inp.localStart);
}

/** One step of the drawn value: `cur` advanced by `dtMs` of wall time. */
export function nextSmoothValue(cur: number, dtMs: number, inp: SmoothInputs, now: number): number {
  if (inp.completed) return 100;
  const p = clamp(inp.progress, 0, 100);
  const c = effectiveCeiling(inp);
  const base = Math.max(cur, p);
  // Something already drew higher than this ceiling allows (an older frame
  // from another bar): hold — the bar never goes backwards.
  if (base >= c) return base;
  const dt = Math.max(0, dtMs);
  // Always: creep toward the ceiling, slowly and ever slower — never still.
  let next = base + (c - base) * (1 - Math.exp(-dt / CREEP_TAU_MS));
  // Behind where the clock says the job should be: follow the clock, up to the
  // reserve below the ceiling, at no more than CHASE_MAX_PACE × the normal pace.
  if (inp.expectedTotalMs !== null && inp.expectedTotalMs > 0) {
    const predicted = (100 * elapsedAt(inp, now)) / inp.expectedTotalMs;
    const knee = c - Math.min(RESERVE_SHARE * (c - p), RESERVE_MAX);
    const target = Math.min(predicted, knee);
    if (target > base) {
      const chase = (target - base) * (1 - Math.exp(-dt / TRACK_TAU_MS));
      const maxStep = (CHASE_MAX_PACE * 100 * dt) / inp.expectedTotalMs;
      next = Math.max(next, base + Math.min(chase, maxStep));
    }
  }
  return Math.min(next, c);
}

/** The numbers a status frame carries, as the smoother reads them. */
export function smoothInputsOf(n: BuildNumbers | null | undefined, receivedAt: number, localStart: number): SmoothInputs {
  const completed = n?.status === "completed";
  return {
    progress: clamp(num(n?.progress) ?? 0, 0, 100),
    ceiling: num(n?.progress_ceiling),
    expectedTotalMs: num(n?.expected_total_ms),
    elapsedMs: num(n?.elapsed_ms),
    receivedAt,
    localStart,
    completed,
  };
}

// ---- shared per-job state ----------------------------------------------------
type Shared = { value: number; at: number; startedAt: number };
const SHARED = new Map<string, Shared>();
const SHARED_MAX = 24;

const isPendingKey = (key: string | null | undefined) => !key || key.startsWith("pending");
/** A pending entry nobody has ticked for this long belongs to an earlier
 *  upload (cancelled, or already handed over) — never to this one. */
const PENDING_STALE_MS = 5000;

/** The live shared entry for a key, dropping a stale pending one. */
function liveEntry(key: string, now: number): Shared | undefined {
  const entry = SHARED.get(key);
  if (entry && isPendingKey(key) && now - entry.at > PENDING_STALE_MS) {
    SHARED.delete(key);
    return undefined;
  }
  return entry;
}

function sharedFor(key: string, seedValue: number, startedAt: number, now: number): Shared {
  let entry = liveEntry(key, now);
  if (!entry) {
    entry = { value: seedValue, at: now, startedAt };
    SHARED.set(key, entry);
    while (SHARED.size > SHARED_MAX) {
      const oldest = SHARED.keys().next().value;
      if (oldest === undefined) break;
      SHARED.delete(oldest);
    }
  }
  return entry;
}

/** Test hook: forget every job's shared value. */
export function resetSmoothProgressForTests() {
  SHARED.clear();
}

export type SmoothProgress = {
  /** The drawn percent, 0-100 (fractional). */
  value: number;
  /** The tick clock (local ms) — time left and elapsed are computed from it. */
  now: number;
  /** Local ms this build was first seen (elapsed fallback). */
  localStart: number;
};

/**
 * The smooth percent for one build. `key` identifies the job; a key that
 * starts with "pending" (files still uploading, no job id yet) hands its value
 * and clock on to the real job id when it arrives, so the bar never restarts.
 * The interval is cleared on unmount.
 */
export function useSmoothProgress(key: string, numbers: BuildNumbers | null | undefined, receivedAt: number): SmoothProgress {
  const [state, setState] = useState<SmoothProgress>(() => {
    const now = Date.now();
    const existing = liveEntry(key, now);
    const p = num(numbers?.progress) ?? 0;
    const completed = numbers?.status === "completed";
    return {
      value: completed ? 100 : Math.max(existing?.value ?? 0, clamp(p, 0, 100)),
      now,
      localStart: existing?.startedAt ?? now,
    };
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const inputRef = useRef({ numbers, receivedAt });
  inputRef.current = { numbers, receivedAt };
  const keyRef = useRef(key);

  // A new key: a pending key hands its value and clock to the real job; any
  // other change is a different job and starts from that job's own value.
  useEffect(() => {
    const prev = keyRef.current;
    if (prev === key) return;
    keyRef.current = key;
    const now = Date.now();
    const apply = (next: SmoothProgress) => { stateRef.current = next; setState(next); };
    const existing = liveEntry(key, now);
    if (existing) {
      apply({ value: existing.value, now, localStart: existing.startedAt });
      return;
    }
    if (isPendingKey(prev)) {
      const carried = SHARED.get(prev);
      const value = Math.max(stateRef.current.value, carried?.value ?? 0);
      const startedAt = carried?.startedAt ?? stateRef.current.localStart;
      SHARED.set(key, { value, at: now, startedAt });
      SHARED.delete(prev); // handed over — the next upload starts clean
      apply({ value, now, localStart: startedAt });
      return;
    }
    apply({ value: 0, now, localStart: now });
  }, [key]);

  useEffect(() => {
    const step = () => {
      const now = Date.now();
      const k = keyRef.current;
      const { numbers: n, receivedAt: at } = inputRef.current;
      const mine = stateRef.current;
      const entry = sharedFor(k, mine.value, mine.localStart, now);
      const cur = Math.max(entry.value, mine.value);
      const dt = Math.max(0, now - entry.at);
      const inp = smoothInputsOf(n, at, entry.startedAt);
      const value = Math.max(cur, nextSmoothValue(cur, dt, inp, now));
      entry.value = value;
      entry.at = now;
      const next = { value, now, localStart: entry.startedAt };
      stateRef.current = next;
      setState(next);
    };
    step();
    const id = setInterval(step, TICK_MS);
    return () => clearInterval(id);
  }, []);

  return state;
}
